import { groupBy } from 'lodash';
import BaseFeature from '../../foundation/base/base';
import { generateSectionFromJson } from './section-template';
import { initCartCookie, getCartAsJson, removeCartItemFromCookie } from '../../foundation/utilities/cartCookie';
import { debounce } from '../../foundation/utilities/debounce';
import { getScreensize } from '../../foundation/utilities/screenSize';

const classes = {
	active: 'o-cart--active',
	open: 'o-cart--open',
	pinned: 'o-cart--pinned',
	slide: 'o-cart--slide-in'
};

export default class Cart extends BaseFeature {
	constructor(element) {
		super(element);
		this.el = element;
		this.body = window.features.dom.body;
		this.initDomCache();
		this.cookieName = 'PSTCART';
		this.removeButtons = null;

		// Pinning option
		this.lastKnownScrollY = 0;
		this.currentScrollY = 0;
		this.ticking = false;
		this.mobileThreshold = 68;
		this.desktopThreshold = 115;

		// Update scroll threshold depending on screen size
		this.scrollDownThreshold = getScreensize() <= 640 ?
			this.mobileThreshold : this.desktopThreshold;

		this.initEvents();
		this.addSubscriptions();
		this.updateCartFromCookies();
		this.attatchResize();
	}

	initEvents() {
		// window.addEventListener('CookieInformationConsentGiven', function (event) { 
		// 	if (CookieInformation.getConsentGivenFor('cookie_cat_necessary')) {
		// 		initCartCookie();
		// 	} 
		// 	}, false); 

		document.addEventListener('scroll', () => this.attachScroll());

		this.addEventListener(this.dom.link, 'click', (e) => {
			e.preventDefault();
			if (this.el.classList.contains(classes.open)) {
				this.close();
			} else {
				this.open();
			}
		});
		this.addEventListener(this.dom.close, 'click', (e) => {
			e.preventDefault();
			this.close();
		});
	}

	addSubscriptions() {
		// Used by AddToCart to trigger update when new item is added
		this.subscribe('/cart/updateCart', () => {
			this.updateCartFromCookies();
		});

		this.subscribe('/header/action/switch', () => {
			if (this.el.classList.contains(classes.open)) {
				this.close();
			}
		});

		this.subscribe('/profile/open', () => {
			this.close();
		});

		this.subscribe('/cart/openCart', () => {
			const open = this.el.classList.contains(classes.open); // Get state
			this.open(); // Try to open
			if (window.pageYOffset > this.scrollDownThreshold) {
				if (!open) {
					this.addSlide(); // If scrolled we should animate if not already open
				}
				this.pin();
			}
		});
	}


	/* ** Scroll and resize events ** */
	attachScroll() {
		this.currentScrollY = window.pageYOffset;
		if (this.currentScrollY < this.scrollDownThreshold) {
			this.requestTick(false);
		} else if (this.currentScrollY > this.scrollDownThreshold) {
			this.requestTick(true);
		}
	}

	attatchResize() {
		const debouncedGetBreakpoint = debounce(() => {
			this.scrollDownThreshold = getScreensize() <= 640 ?
				this.mobileThreshold : this.desktopThreshold;
		}, 250);

		window.addEventListener('resize', debouncedGetBreakpoint);
	}

	/* ** Handle scroll tick ** */
	requestTick(pin) {
		if (!this.ticking && !pin) {
			requestAnimationFrame(this.unPin.bind(this));
		} else if (!this.ticking && pin) {
			requestAnimationFrame(this.pin.bind(this));
		}
		this.ticking = true;
	}

	updateRemoveLinks() {
		this.removeButtons = this.el.querySelectorAll('[data-js="cart__remove"]');
		this.removeButtons.forEach((item) => {
			this.addEventListener(item, 'click', (e) => {
				e.stopPropagation();
				e.preventDefault();
				this.removeItem(item);
			});
		});
	}

	/* Cart item functions */
	updateCartFromCookies() {
		const cart = getCartAsJson();
		if (!cart) return;
		const sections = [];
		let count = 0;
		Object.keys(cart).forEach((key) => {
			count += Object.keys(cart[key]).length; // Update count variable for each item
			// For each section in cart, sort items based on section name
			const group = groupBy(cart[key], item => item.section.name);
			Object.keys(group).forEach((item) => {
				sections.push(generateSectionFromJson(group[item]));
			});
		});
		// Add html to cart content
		this.dom.contentTarget.innerHTML = sections.join('');
		this.setCartCount(count); // Update icon with count
		this.updateRemoveLinks(); // Update remove links
	}

	removeItem(item) {
		const productId = item.getAttribute('data-product-id');
		const sectionId = item.getAttribute('data-section-id');
		const removed = removeCartItemFromCookie(productId, sectionId);
		if (removed) {
			this.updateCartFromCookies(); // Update cart content
			this.publish('/cart/updateAddLinks'); // Update page links
			this.publish('/cart/removeItemFromCheckout', { id: productId, section: sectionId }); // Remove item from checkout page
		}
	}

	setCartCount(count) {
		if (count > 0) {
			this.setActiveState();
		} else {
			this.setNonActiveState();
		}
		this.dom.count.innerHTML = count;
	}


	/* Pinning functions */
	pin() {
		if (!this.el.classList.contains(classes.pinned) && this.el.classList.contains(classes.open)) {
			this.el.classList.add(classes.pinned);
		}
		setTimeout(() => {
			this.removeSlide();
		}, 250); // This time equals the animation time corresponding in the stylesheet of cart
		this.ticking = false;
	}

	unPin() {
		if (this.el.classList.contains(classes.pinned)) {
			this.el.classList.remove(classes.pinned);
		}
		this.ticking = false;
	}


	/* Slide functions */
	addSlide() {
		if (!this.el.classList.contains(classes.slide)) {
			this.el.classList.add(classes.slide);
		}
	}

	removeSlide() {
		if (this.el.classList.contains(classes.slide)) {
			this.el.classList.remove(classes.slide);
		}
	}

	/* State of cart icon functions */
	setNonActiveState() {
		if (this.el.classList.contains(classes.active)) this.el.classList.remove(classes.active);
	}

	setActiveState() {
		if (!this.el.classList.contains(classes.active)) this.el.classList.add(classes.active);
	}

	/* Toggle functions */
	toggle() {
		if (this.el.classList.contains(classes.open)) {
			this.close();
		} else {
			this.open();
		}
	}

	open() {
		this.publish('/cart/open')
		if (!this.el.classList.contains(classes.open)) this.el.classList.add(classes.open);
	}

	close() {
		if (this.el.classList.contains(classes.open)) {
			// Remove open and pinned classes
			this.el.classList.remove(classes.open);
			this.el.classList.remove(classes.pinned);
		}
	}
}
